import React, { useCallback, useEffect, useState } from "react";
import { CloudArrowUp, FilmStrip, Image as ImageIcon, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const MAX_VIDEO_SECONDS = 5;
const MAX_SIZE_MB = 100;

const isVideo = (file) => file && file.type && file.type.startsWith("video/");
const isImage = (file) => file && file.type && file.type.startsWith("image/");

const validateVideoDuration = (file) =>
  new Promise((resolve) => {
    const v = document.createElement("video");
    v.preload = "metadata";
    let settled = false;
    const finish = (dur) => {
      if (settled) return;
      settled = true;
      try { URL.revokeObjectURL(v.src); } catch (_) {}
      resolve(dur);
    };
    v.onloadedmetadata = () => {
      if (Number.isFinite(v.duration) && v.duration > 0) finish(v.duration);
    };
    v.ondurationchange = () => {
      if (Number.isFinite(v.duration) && v.duration > 0) finish(v.duration);
    };
    v.onerror = () => finish(null);
    // Safety timeout — if metadata never resolves, fail closed.
    setTimeout(() => finish(null), 4000);
    v.src = URL.createObjectURL(file);
  });

export default function UploadZone({
  label,
  accept,
  hint,
  file,
  onFile,
  // 'image' | 'video' | 'media' (auto: video OR image)
  icon = "image",
  testId,
}) {
  const [drag, setDrag] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Whenever the file prop changes (e.g. parent resets), keep preview in sync.
  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handle = useCallback(
    async (f) => {
      if (!f) return;
      // size check
      if (f.size > MAX_SIZE_MB * 1024 * 1024) {
        toast.error(`File too large. Max ${MAX_SIZE_MB} MB.`);
        return;
      }
      // type check
      if (icon === "video" && !isVideo(f)) {
        toast.error("Please upload a video file");
        return;
      }
      if (icon === "image" && !isImage(f)) {
        toast.error("Please upload an image file");
        return;
      }
      if (icon === "media" && !isVideo(f) && !isImage(f)) {
        toast.error("Please upload a video or image file");
        return;
      }
      // video duration check
      if (isVideo(f)) {
        const dur = await validateVideoDuration(f);
        if (dur === null) {
          toast.error("Could not read video duration. Please try a different file.");
          return;
        }
        if (dur > MAX_VIDEO_SECONDS + 0.3) {
          toast.error(`Video too long (${dur.toFixed(1)}s). Max ${MAX_VIDEO_SECONDS} seconds.`);
          return;
        }
      }
      onFile(f);
    },
    [icon, onFile]
  );

  // Decide preview element type from the *actual* file MIME, not the icon prop.
  const previewKind = isVideo(file) ? "video" : "image";
  const HeaderIcon = previewKind === "video" ? FilmStrip : ImageIcon;

  return (
    <div
      data-testid={testId}
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        const f = e.dataTransfer.files?.[0];
        handle(f);
      }}
      className={`relative rounded-2xl border-2 border-dashed transition-all ${
        drag ? "border-red-400 bg-red-500/5" : "border-white/10 hover:border-white/20"
      } bg-white/[0.02]`}
    >
      <label className="block cursor-pointer p-6 sm:p-8">
        <input
          type="file"
          accept={accept}
          className="hidden"
          data-testid={`${testId}-input`}
          onChange={(e) => handle(e.target.files?.[0])}
        />

        {!file ? (
          <div className="flex flex-col items-center text-center gap-3 py-6">
            <div className="w-14 h-14 rounded-2xl bg-white/[0.04] border border-white/10 flex items-center justify-center">
              <CloudArrowUp size={26} weight="duotone" className="text-red-400" />
            </div>
            <div>
              <div className="text-base font-medium text-white">{label}</div>
              <div className="text-xs text-zinc-500 mt-1 font-mono tracking-wider uppercase">
                {hint}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs min-w-0">
                <HeaderIcon size={18} className="text-red-400 shrink-0" />
                <span
                  className="font-mono text-zinc-300 truncate max-w-[200px]"
                  data-testid={`${testId}-filename`}
                >
                  {file.name}
                </span>
                <span className="text-zinc-500 shrink-0">
                  {(file.size / (1024 * 1024)).toFixed(1)} MB
                </span>
              </div>
              <button
                type="button"
                data-testid={`${testId}-remove`}
                onClick={(e) => {
                  e.preventDefault();
                  onFile(null);
                  setPreviewUrl(null);
                }}
                className="text-zinc-500 hover:text-red-400"
              >
                <X size={16} />
              </button>
            </div>
            <div className="rounded-lg overflow-hidden bg-black aspect-video flex items-center justify-center">
              {previewKind === "video" ? (
                <video
                  src={previewUrl}
                  controls
                  className="max-h-44 w-full object-contain"
                  data-testid={`${testId}-preview-video`}
                />
              ) : (
                <img
                  src={previewUrl}
                  alt={file.name || "preview"}
                  className="max-h-44 w-full object-contain"
                  data-testid={`${testId}-preview-image`}
                />
              )}
            </div>
          </div>
        )}
      </label>
    </div>
  );
}
